/**
 * Capability Definitions
 * 
 * Complete catalogue of all available capabilities (integrations) in YourDreamAI.
 * Each capability is a permanent unlock that can be purchased once.
 * 
 * Pricing Bands:
 * - Micro: $4.99 (read-only or low-risk write, immediate benefit, zero config)
 * - Power: $9.99 - $19.99 (cross-app logic, some write actions, requires approval)
 * - Authority: $29 - $50 (high leverage, business-grade value, gated behind Pro/Business)
 */

import type { Integration } from "../platform/types";

export type CapabilityCategory = 
  | "identity_auth"
  | "payments_finance"
  | "messaging_voice"
  | "social_ads"
  | "media_entertainment"
  | "maps_geo"
  | "ai_ml"
  | "cloud_devops"
  | "developer"
  | "productivity"
  | "crm_sales"
  | "ecommerce"
  | "reviews_reputation"
  | "hr_payroll"
  | "education"
  | "iot"
  | "vehicles"
  | "healthcare"
  | "government";

export interface Capability {
  id: string;
  name: string;
  description: string;
  category: CapabilityCategory;
  pricingBand: "micro" | "power" | "authority";
  unlockPriceUSD: number;
  requiredSubscriptionTier?: "free" | "personal" | "pro" | "business";
  riskLevel: "low" | "medium" | "high";
  actionTypes: ("read" | "write" | "read_write")[];
  permissionScopes: string[];
  requiresApproval: boolean;
  documentationUrl?: string;
  icon: string;
  enabled: boolean; // Feature flag
  phase: 1 | 2 | "future"; // Implementation phase
  partnerOnly?: boolean; // Requires partner/enterprise access
}

/**
 * Phase 1 - Core / High-Demand Capabilities
 */
export const PHASE_1_CAPABILITIES: Capability[] = [
  // Media & Entertainment
  {
    id: "spotify_control",
    name: "Spotify Control Pack",
    description: "Control your Spotify playback, create playlists, and manage your music library.",
    category: "media_entertainment",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["user-read-playback-state", "user-modify-playback-state", "playlist-read-private", "playlist-modify-public"],
    requiresApproval: false,
    documentationUrl: "https://developer.spotify.com/documentation/web-api",
    icon: "https://cdn.simpleicons.org/spotify",
    enabled: false,
    phase: 1,
  },
  // Productivity - Google Workspace
  {
    id: "gmail_summary",
    name: "Gmail Summary Pack",
    description: "Read and summarize your Gmail inbox. Get AI-powered email insights and organization.",
    category: "productivity",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["https://www.googleapis.com/auth/gmail.readonly"],
    requiresApproval: false,
    documentationUrl: "https://developers.google.com/gmail/api",
    icon: "https://cdn.simpleicons.org/gmail",
    enabled: false,
    phase: 1,
  },
  {
    id: "google_calendar_sync",
    name: "Calendar Sync Pack",
    description: "Sync and manage your Google Calendar. Schedule meetings and get reminders.",
    category: "productivity",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["https://www.googleapis.com/auth/calendar.readonly", "https://www.googleapis.com/auth/calendar.events"],
    requiresApproval: false,
    documentationUrl: "https://developers.google.com/calendar/api",
    icon: "https://cdn.simpleicons.org/googlecalendar",
    enabled: false,
    phase: 1,
  },
  {
    id: "google_drive_read",
    name: "Google Drive Read Pack",
    description: "Read files from your Google Drive. Access documents, spreadsheets, and more.",
    category: "productivity",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["https://www.googleapis.com/auth/drive.readonly"],
    requiresApproval: false,
    documentationUrl: "https://developers.google.com/drive/api",
    icon: "https://cdn.simpleicons.org/googledrive",
    enabled: false,
    phase: 1,
  },
  // Messaging
  {
    id: "telegram_bot",
    name: "Telegram Bot Pack",
    description: "Create and control Telegram bots. Send messages and manage conversations.",
    category: "messaging_voice",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["bot"],
    requiresApproval: true,
    documentationUrl: "https://core.telegram.org/bots/api",
    icon: "https://cdn.simpleicons.org/telegram",
    enabled: false,
    phase: 1,
  },
  {
    id: "discord_control",
    name: "Discord Control Pack",
    description: "Control Discord servers, send messages, and manage channels.",
    category: "messaging_voice",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["bot", "guilds", "messages"],
    requiresApproval: true,
    documentationUrl: "https://discord.com/developers/docs",
    icon: "https://cdn.simpleicons.org/discord",
    enabled: false,
    phase: 1,
  },
  {
    id: "slack_control",
    name: "Slack Control Pack",
    description: "Send messages, manage channels, and automate Slack workflows.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["channels:read", "chat:write", "users:read"],
    requiresApproval: true,
    documentationUrl: "https://api.slack.com/docs",
    icon: "https://cdn.simpleicons.org/slack",
    enabled: false,
    phase: 1,
  },
  // Microsoft 365
  {
    id: "microsoft_365",
    name: "Microsoft 365 Pack",
    description: "Access Outlook, OneDrive, and Teams. Full Microsoft 365 integration.",
    category: "productivity",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["Mail.Read", "Files.ReadWrite", "Calendars.ReadWrite"],
    requiresApproval: true,
    documentationUrl: "https://learn.microsoft.com/en-us/graph/overview",
    icon: "https://cdn.simpleicons.org/microsoft",
    enabled: false,
    phase: 1,
  },
  // Payments & Finance
  {
    id: "stripe_readonly",
    name: "Stripe Read Pack",
    description: "Read-only access to Stripe payments, customers, and transactions.",
    category: "payments_finance",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    documentationUrl: "https://stripe.com/docs/api",
    icon: "https://cdn.simpleicons.org/stripe",
    enabled: false,
    phase: 1,
  },
  {
    id: "crypto_intelligence",
    name: "Crypto Intelligence Pack",
    description: "Read-only access to Binance and Coinbase. Track prices and portfolio.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 29.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    documentationUrl: "https://binance-docs.github.io/apidocs/spot/en/",
    icon: "https://cdn.simpleicons.org/binance",
    enabled: false,
    phase: 1,
  },
  // Reviews & Reputation
  {
    id: "google_business_reviews",
    name: "Google Business Reviews Pack",
    description: "Monitor and respond to Google Business Profile reviews.",
    category: "reviews_reputation",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["https://www.googleapis.com/auth/business.manage"],
    requiresApproval: true,
    documentationUrl: "https://developers.google.com/my-business/content/overview",
    icon: "https://cdn.simpleicons.org/googlebusinessprofile",
    enabled: false,
    phase: 1,
    partnerOnly: true, // Requires Google Business Profile API access
  },
  {
    id: "yelp_reviews",
    name: "Yelp Reviews Pack",
    description: "Monitor and respond to Yelp business reviews.",
    category: "reviews_reputation",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["business"],
    requiresApproval: true,
    documentationUrl: "https://docs.developer.yelp.com/docs/fusion-intro",
    icon: "https://cdn.simpleicons.org/yelp",
    enabled: false,
    phase: 1,
  },
  // Social Media
  {
    id: "social_posting",
    name: "Social Posting Pack",
    description: "Post to Meta, X (Twitter), and LinkedIn. Schedule and manage social content.",
    category: "social_ads",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["pages_manage_posts", "tweet.write", "w_member_social"],
    requiresApproval: true,
    documentationUrl: "https://developers.facebook.com/docs/graph-api",
    icon: "https://cdn.simpleicons.org/meta",
    enabled: false,
    phase: 1,
  },
  {
    id: "linkedin_api",
    name: "LinkedIn Integration Pack",
    description: "Access LinkedIn API for posting and engagement (where permitted).",
    category: "social_ads",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["r_liteprofile", "w_member_social"],
    requiresApproval: true,
    documentationUrl: "https://learn.microsoft.com/en-us/linkedin/",
    icon: "https://cdn.simpleicons.org/linkedin",
    enabled: false,
    phase: 1,
    partnerOnly: true, // LinkedIn API requires partner approval
  },
];

/**
 * Phase 2 - Power / Business Capabilities
 */
export const PHASE_2_CAPABILITIES: Capability[] = [
  // CRM
  {
    id: "hubspot_read",
    name: "HubSpot Read Pack",
    description: "Read-only access to HubSpot CRM. View contacts, deals, and companies.",
    category: "crm_sales",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["contacts", "deals", "companies"],
    requiresApproval: true,
    documentationUrl: "https://developers.hubspot.com/docs/api/overview",
    icon: "https://cdn.simpleicons.org/hubspot",
    enabled: false,
    phase: 2,
  },
  {
    id: "salesforce_read",
    name: "Salesforce Read Pack",
    description: "Read-only access to Salesforce. View leads, opportunities, and accounts.",
    category: "crm_sales",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["api", "read"],
    requiresApproval: true,
    documentationUrl: "https://developer.salesforce.com/docs/apis",
    icon: "https://cdn.simpleicons.org/salesforce",
    enabled: false,
    phase: 2,
  },
  // Accounting
  {
    id: "xero_read",
    name: "Xero Read Pack",
    description: "Read-only access to Xero accounting. View invoices, bills, and financial data.",
    category: "payments_finance",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["accounting.transactions", "accounting.contacts.read"],
    requiresApproval: true,
    documentationUrl: "https://developer.xero.com/documentation",
    icon: "https://cdn.simpleicons.org/xero",
    enabled: false,
    phase: 2,
  },
  {
    id: "quickbooks_read",
    name: "QuickBooks Read Pack",
    description: "Read-only access to QuickBooks. View financial reports and transactions.",
    category: "payments_finance",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["com.intuit.quickbooks.accounting"],
    requiresApproval: true,
    documentationUrl: "https://developer.intuit.com/app/developer/qbo/docs",
    icon: "https://cdn.simpleicons.org/quickbooks",
    enabled: false,
    phase: 2,
  },
  // Payments & Invoicing
  {
    id: "stripe_billing",
    name: "Stripe Billing Pack",
    description: "Full Stripe billing and invoicing. Create subscriptions and manage payments.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["read_write"],
    requiresApproval: true,
    documentationUrl: "https://stripe.com/docs/billing",
    icon: "https://cdn.simpleicons.org/stripe",
    enabled: false,
    phase: 2,
  },
  // Ads & Marketing
  {
    id: "google_ads_reporting",
    name: "Google Ads Reporting Pack",
    description: "Read Google Ads performance data. Track campaigns and ROI.",
    category: "social_ads",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["https://www.googleapis.com/auth/adwords"],
    requiresApproval: true,
    documentationUrl: "https://developers.google.com/google-ads/api/docs/start",
    icon: "https://cdn.simpleicons.org/googleads",
    enabled: false,
    phase: 2,
  },
  {
    id: "meta_ads_reporting",
    name: "Meta Ads Reporting Pack",
    description: "Read Meta Ads performance data. Track Facebook and Instagram campaigns.",
    category: "social_ads",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["ads_read", "ads_management"],
    requiresApproval: true,
    documentationUrl: "https://developers.facebook.com/docs/marketing-apis",
    icon: "https://cdn.simpleicons.org/meta",
    enabled: false,
    phase: 2,
  },
  // Team Collaboration
  {
    id: "multi_calendar",
    name: "Multi-Calendar Control Pack",
    description: "Aggregate and manage multiple calendars (Google, Outlook, Apple).",
    category: "productivity",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["calendar.readwrite"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/googlecalendar",
    enabled: false,
    phase: 2,
  },
  {
    id: "multi_inbox",
    name: "Multi-Inbox Control Pack",
    description: "Aggregate and manage multiple email inboxes (Gmail, Outlook, etc.).",
    category: "productivity",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["mail.readwrite"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/gmail",
    enabled: false,
    phase: 2,
  },
  {
    id: "team_messaging_control",
    name: "Team Messaging Control Pack",
    description: "Control Slack and Teams workspaces. Manage channels and automate workflows.",
    category: "messaging_voice",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["channels:manage", "chat:write", "team:read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/slack",
    enabled: false,
    phase: 2,
  },
  {
    id: "email_task_automation",
    name: "Email → Task Automation Pack",
    description: "Automatically convert emails to tasks. Link Gmail, Outlook, and task managers.",
    category: "productivity",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["mail.read", "tasks"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/gmail",
    enabled: false,
    phase: 2,
  },
  {
    id: "expense_tracking",
    name: "Expense Tracking Pack",
    description: "Track expenses across Ramp, Brex, and other expense platforms.",
    category: "payments_finance",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/ramp",
    enabled: false,
    phase: 2,
  },
];

/**
 * Phase 3 - Future / Complete Catalogue Capabilities
 * ALL 200+ APIs from the capability catalogue
 */
export const FUTURE_CAPABILITIES: Capability[] = [
  // Identity, Auth, Security
  {
    id: "oauth_google",
    name: "Google OAuth Pack",
    description: "Authenticate users with Google. Access Google services on behalf of users.",
    category: "identity_auth",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["openid", "profile", "email"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/google",
    enabled: false,
    phase: "future",
  },
  {
    id: "oauth_apple",
    name: "Apple Sign In Pack",
    description: "Authenticate users with Apple. Secure authentication for iOS and web.",
    category: "identity_auth",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["name", "email"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/apple",
    enabled: false,
    phase: "future",
  },
  {
    id: "oauth_microsoft",
    name: "Microsoft OAuth Pack",
    description: "Authenticate users with Microsoft. Access Microsoft 365 services.",
    category: "identity_auth",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["openid", "profile", "email"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/microsoft",
    enabled: false,
    phase: "future",
  },
  {
    id: "sso_okta",
    name: "Okta SSO Pack",
    description: "Single Sign-On with Okta. Enterprise identity management.",
    category: "identity_auth",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["openid", "profile"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/okta",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "sso_azure_ad",
    name: "Azure AD SSO Pack",
    description: "Single Sign-On with Azure Active Directory. Enterprise authentication.",
    category: "identity_auth",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["openid", "profile"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/microsoftazure",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "passkeys",
    name: "Passkeys Pack",
    description: "Passwordless authentication with Passkeys/WebAuthn. Modern secure login.",
    category: "identity_auth",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["webauthn"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/passkeys",
    enabled: false,
    phase: "future",
  },
  {
    id: "mfa_twilio",
    name: "Twilio MFA Pack",
    description: "Multi-factor authentication via SMS/voice with Twilio.",
    category: "identity_auth",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["verify"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/twilio",
    enabled: false,
    phase: "future",
  },
  // Payments, Banking, Finance - Additional
  {
    id: "adyen",
    name: "Adyen Payments Pack",
    description: "Process payments with Adyen. Global payment processing.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["payments"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/adyen",
    enabled: false,
    phase: "future",
  },
  {
    id: "braintree",
    name: "Braintree Payments Pack",
    description: "Process payments with Braintree. PayPal and credit card processing.",
    category: "payments_finance",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["payments"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/braintree",
    enabled: false,
    phase: "future",
  },
  {
    id: "plaid",
    name: "Plaid Banking Pack",
    description: "Connect to bank accounts via Plaid. Read account balances and transactions.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["transactions", "accounts"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/plaid",
    enabled: false,
    phase: "future",
  },
  {
    id: "truelayer",
    name: "TrueLayer Banking Pack",
    description: "Connect to bank accounts via TrueLayer. Open Banking integration.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["accounts", "transactions"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/truelayer",
    enabled: false,
    phase: "future",
  },
  {
    id: "chargebee",
    name: "Chargebee Billing Pack",
    description: "Subscription billing and revenue management with Chargebee.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["read_write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/chargebee",
    enabled: false,
    phase: "future",
  },
  {
    id: "recurly",
    name: "Recurly Billing Pack",
    description: "Subscription billing and revenue management with Recurly.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["read_write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/recurly",
    enabled: false,
    phase: "future",
  },
  {
    id: "myob",
    name: "MYOB Accounting Pack",
    description: "Read-only access to MYOB accounting. View financial data.",
    category: "payments_finance",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/myob",
    enabled: false,
    phase: "future",
  },
  {
    id: "ramp",
    name: "Ramp Expense Pack",
    description: "Track corporate expenses with Ramp. View cards and transactions.",
    category: "payments_finance",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/ramp",
    enabled: false,
    phase: "future",
  },
  {
    id: "brex",
    name: "Brex Expense Pack",
    description: "Track corporate expenses with Brex. View cards and spending.",
    category: "payments_finance",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/brex",
    enabled: false,
    phase: "future",
  },
  {
    id: "airwallex",
    name: "Airwallex Payments Pack",
    description: "Global payments and FX with Airwallex.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["payments"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/airwallex",
    enabled: false,
    phase: "future",
  },
  {
    id: "wise",
    name: "Wise Payments Pack",
    description: "International money transfers with Wise.",
    category: "payments_finance",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["transfers"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/wise",
    enabled: false,
    phase: "future",
  },
  {
    id: "avalara",
    name: "Avalara Tax Pack",
    description: "Automated tax calculation and compliance with Avalara.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/avalara",
    enabled: false,
    phase: "future",
  },
  {
    id: "taxjar",
    name: "TaxJar Tax Pack",
    description: "Sales tax automation and compliance with TaxJar.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/taxjar",
    enabled: false,
    phase: "future",
  },
  {
    id: "polygon",
    name: "Polygon Market Data Pack",
    description: "Access market data via Polygon. Real-time stock prices and financial data.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/polygon",
    enabled: false,
    phase: "future",
  },
  {
    id: "iex",
    name: "IEX Cloud Market Data Pack",
    description: "Access market data via IEX Cloud. Stock prices and financial data.",
    category: "payments_finance",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/iexcloud",
    enabled: false,
    phase: "future",
  },
  {
    id: "alpha_vantage",
    name: "Alpha Vantage Market Data Pack",
    description: "Access market data via Alpha Vantage. Stock prices and financial indicators.",
    category: "payments_finance",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/alphavantage",
    enabled: false,
    phase: "future",
  },
  {
    id: "interactive_brokers",
    name: "Interactive Brokers Pack",
    description: "Connect to Interactive Brokers trading platform. Read account and positions.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/interactivebrokers",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "alpaca",
    name: "Alpaca Trading Pack",
    description: "Connect to Alpaca trading platform. Read account and market data.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/alpaca",
    enabled: false,
    phase: "future",
  },
  {
    id: "kraken",
    name: "Kraken Crypto Pack",
    description: "Read-only access to Kraken. Track prices and portfolio.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 29.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/kraken",
    enabled: false,
    phase: "future",
  },
  {
    id: "okx",
    name: "OKX Crypto Pack",
    description: "Read-only access to OKX. Track prices and portfolio.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 29.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/okx",
    enabled: false,
    phase: "future",
  },
  {
    id: "bybit",
    name: "Bybit Crypto Pack",
    description: "Read-only access to Bybit. Track prices and portfolio.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 29.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/bybit",
    enabled: false,
    phase: "future",
  },
  {
    id: "fireblocks",
    name: "Fireblocks Crypto Custody Pack",
    description: "Enterprise crypto custody and security with Fireblocks.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/fireblocks",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "bitgo",
    name: "BitGo Crypto Custody Pack",
    description: "Enterprise crypto custody and security with BitGo.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/bitgo",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "stripe_radar",
    name: "Stripe Radar Fraud Pack",
    description: "Fraud detection and prevention with Stripe Radar.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/stripe",
    enabled: false,
    phase: "future",
  },
  {
    id: "sift",
    name: "Sift Fraud Prevention Pack",
    description: "Fraud detection and prevention with Sift.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/sift",
    enabled: false,
    phase: "future",
  },
  {
    id: "riskified",
    name: "Riskified Fraud Prevention Pack",
    description: "Fraud detection and prevention with Riskified.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/riskified",
    enabled: false,
    phase: "future",
  },
  // E-Commerce Platforms
  {
    id: "shopify",
    name: "Shopify Store Pack",
    description: "Manage Shopify stores. Sync products, process orders, manage customers.",
    category: "ecommerce",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read_products", "write_products", "read_orders", "write_orders"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/shopify",
    enabled: false,
    phase: "future",
  },
  {
    id: "woocommerce",
    name: "WooCommerce Store Pack",
    description: "Manage WooCommerce stores. Sync products, process orders, manage customers.",
    category: "ecommerce",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/woocommerce",
    enabled: false,
    phase: "future",
  },
  {
    id: "bigcommerce",
    name: "BigCommerce Store Pack",
    description: "Manage BigCommerce stores. Sync products, process orders, manage customers.",
    category: "ecommerce",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/bigcommerce",
    enabled: false,
    phase: "future",
  },
  {
    id: "square",
    name: "Square Store Pack",
    description: "Manage Square stores. Accept payments, manage inventory, process orders.",
    category: "ecommerce",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["payments", "inventory", "orders"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/square",
    enabled: false,
    phase: "future",
  },
  {
    id: "etsy",
    name: "Etsy Store Pack",
    description: "Manage Etsy stores. List products, process orders, manage inventory.",
    category: "ecommerce",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/etsy",
    enabled: false,
    phase: "future",
  },
  {
    id: "amazon",
    name: "Amazon Seller Pack",
    description: "Manage Amazon Seller Central. List products, process orders, manage inventory.",
    category: "ecommerce",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/amazon",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "ebay",
    name: "eBay Store Pack",
    description: "Manage eBay stores. List products, process orders, manage inventory.",
    category: "ecommerce",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/ebay",
    enabled: false,
    phase: "future",
  },
  {
    id: "shippo",
    name: "Shippo Shipping Pack",
    description: "Manage shipping with Shippo. Create labels, track packages, calculate rates.",
    category: "ecommerce",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/shippo",
    enabled: false,
    phase: "future",
  },
  {
    id: "shipstation",
    name: "ShipStation Shipping Pack",
    description: "Manage shipping with ShipStation. Create labels, track packages.",
    category: "ecommerce",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/shipstation",
    enabled: false,
    phase: "future",
  },
  {
    id: "aftership",
    name: "AfterShip Tracking Pack",
    description: "Track packages with AfterShip. Monitor shipments across carriers.",
    category: "ecommerce",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/aftership",
    enabled: false,
    phase: "future",
  },
  // Messaging, Voice, Comms
  {
    id: "twilio_sms",
    name: "Twilio SMS Pack",
    description: "Send and receive SMS messages with Twilio.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["sms"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/twilio",
    enabled: false,
    phase: "future",
  },
  {
    id: "twilio_voice",
    name: "Twilio Voice Pack",
    description: "Make and receive voice calls with Twilio.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["voice"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/twilio",
    enabled: false,
    phase: "future",
  },
  {
    id: "sendgrid",
    name: "SendGrid Email Pack",
    description: "Send transactional emails with SendGrid.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["mail.send"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/sendgrid",
    enabled: false,
    phase: "future",
  },
  {
    id: "mailgun",
    name: "Mailgun Email Pack",
    description: "Send transactional emails with Mailgun.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["mail.send"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/mailgun",
    enabled: false,
    phase: "future",
  },
  {
    id: "postmark",
    name: "Postmark Email Pack",
    description: "Send transactional emails with Postmark.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["mail.send"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/postmark",
    enabled: false,
    phase: "future",
  },
  {
    id: "fcm",
    name: "Firebase Cloud Messaging Pack",
    description: "Send push notifications to Android and iOS with FCM.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["fcm"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/firebase",
    enabled: false,
    phase: "future",
  },
  {
    id: "apns",
    name: "Apple Push Notification Pack",
    description: "Send push notifications to iOS devices with APNs.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["apns"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/apple",
    enabled: false,
    phase: "future",
  },
  {
    id: "onesignal",
    name: "OneSignal Push Pack",
    description: "Send push notifications across platforms with OneSignal.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["notifications"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/onesignal",
    enabled: false,
    phase: "future",
  },
  {
    id: "whatsapp",
    name: "WhatsApp Business Pack",
    description: "Send messages via WhatsApp Business API.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["whatsapp_business"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/whatsapp",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "microsoft_teams",
    name: "Microsoft Teams Pack",
    description: "Send messages and manage Teams channels.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["chat", "channels"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/microsoftteams",
    enabled: false,
    phase: "future",
  },
  {
    id: "zoom",
    name: "Zoom Meetings Pack",
    description: "Create and manage Zoom meetings.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["meeting:write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/zoom",
    enabled: false,
    phase: "future",
  },
  // Social, Ads, Growth
  {
    id: "tiktok",
    name: "TikTok Content Pack",
    description: "Post content to TikTok. Schedule videos and manage account.",
    category: "social_ads",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["video.upload", "user.info"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/tiktok",
    enabled: false,
    phase: "future",
  },
  {
    id: "tiktok_ads",
    name: "TikTok Ads Pack",
    description: "Manage TikTok ad campaigns. Track performance and optimize.",
    category: "social_ads",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["ads.read", "ads.write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/tiktok",
    enabled: false,
    phase: "future",
  },
  {
    id: "youtube",
    name: "YouTube Content Pack",
    description: "Upload videos to YouTube. Manage channel and analytics.",
    category: "social_ads",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["youtube.upload", "youtube.manage"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/youtube",
    enabled: false,
    phase: "future",
  },
  {
    id: "reddit",
    name: "Reddit Content Pack",
    description: "Post to Reddit. Manage submissions and comments.",
    category: "social_ads",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["submit", "read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/reddit",
    enabled: false,
    phase: "future",
  },
  {
    id: "pinterest",
    name: "Pinterest Content Pack",
    description: "Post pins to Pinterest. Manage boards and analytics.",
    category: "social_ads",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["pins:read", "pins:write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/pinterest",
    enabled: false,
    phase: "future",
  },
  {
    id: "snapchat_ads",
    name: "Snapchat Ads Pack",
    description: "Manage Snapchat ad campaigns. Track performance.",
    category: "social_ads",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["ads.read", "ads.write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/snapchat",
    enabled: false,
    phase: "future",
  },
  {
    id: "ga4",
    name: "Google Analytics 4 Pack",
    description: "Access Google Analytics 4 data. Track website performance.",
    category: "social_ads",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["analytics.readonly"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/googleanalytics",
    enabled: false,
    phase: "future",
  },
  {
    id: "mixpanel",
    name: "Mixpanel Analytics Pack",
    description: "Access Mixpanel analytics data. Track user behavior.",
    category: "social_ads",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/mixpanel",
    enabled: false,
    phase: "future",
  },
  {
    id: "amplitude",
    name: "Amplitude Analytics Pack",
    description: "Access Amplitude analytics data. Track product usage.",
    category: "social_ads",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/amplitude",
    enabled: false,
    phase: "future",
  },
  {
    id: "segment",
    name: "Segment CDP Pack",
    description: "Access Segment customer data platform. Track user journeys.",
    category: "social_ads",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/segment",
    enabled: false,
    phase: "future",
  },
  {
    id: "braze",
    name: "Braze Marketing Pack",
    description: "Send marketing campaigns with Braze. Multi-channel messaging.",
    category: "social_ads",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["campaigns", "users"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/braze",
    enabled: false,
    phase: "future",
  },
  // Media, Entertainment, Content
  {
    id: "apple_music",
    name: "Apple Music Pack",
    description: "Access Apple Music. Search and play music.",
    category: "media_entertainment",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["music.readonly"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/applemusic",
    enabled: false,
    phase: "future",
  },
  {
    id: "soundcloud",
    name: "SoundCloud Pack",
    description: "Access SoundCloud. Search and play tracks.",
    category: "media_entertainment",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/soundcloud",
    enabled: false,
    phase: "future",
  },
  {
    id: "twitch",
    name: "Twitch Pack",
    description: "Access Twitch. View streams and manage channel.",
    category: "media_entertainment",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["channel:read", "channel:manage"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/twitch",
    enabled: false,
    phase: "future",
  },
  {
    id: "vimeo",
    name: "Vimeo Pack",
    description: "Upload and manage videos on Vimeo.",
    category: "media_entertainment",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["video.upload", "video.manage"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/vimeo",
    enabled: false,
    phase: "future",
  },
  {
    id: "podcast_apple",
    name: "Apple Podcasts Pack",
    description: "Access Apple Podcasts directory. Search and retrieve podcast data.",
    category: "media_entertainment",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/applepodcasts",
    enabled: false,
    phase: "future",
  },
  {
    id: "podcast_spotify",
    name: "Spotify Podcasts Pack",
    description: "Access Spotify Podcasts directory. Search and retrieve podcast data.",
    category: "media_entertainment",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/spotify",
    enabled: false,
    phase: "future",
  },
  // Maps, Geo, Travel, Local
  {
    id: "google_maps",
    name: "Google Maps Pack",
    description: "Access Google Maps. Geocoding, directions, places search.",
    category: "maps_geo",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["maps"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/googlemaps",
    enabled: false,
    phase: "future",
  },
  {
    id: "mapbox",
    name: "Mapbox Maps Pack",
    description: "Access Mapbox. Custom maps, geocoding, directions.",
    category: "maps_geo",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/mapbox",
    enabled: false,
    phase: "future",
  },
  {
    id: "here_maps",
    name: "Here Maps Pack",
    description: "Access Here Maps. Geocoding, routing, places.",
    category: "maps_geo",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/here",
    enabled: false,
    phase: "future",
  },
  {
    id: "openweather",
    name: "OpenWeather Pack",
    description: "Access weather data from OpenWeather.",
    category: "maps_geo",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/openweathermap",
    enabled: false,
    phase: "future",
  },
  {
    id: "weatherkit",
    name: "WeatherKit Pack",
    description: "Access Apple WeatherKit. Weather data and forecasts.",
    category: "maps_geo",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/apple",
    enabled: false,
    phase: "future",
  },
  {
    id: "trustpilot",
    name: "Trustpilot Reviews Pack",
    description: "Monitor and respond to Trustpilot reviews.",
    category: "reviews_reputation",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/trustpilot",
    enabled: false,
    phase: "future",
  },
  {
    id: "podium",
    name: "Podium Reviews Pack",
    description: "Monitor and respond to reviews via Podium.",
    category: "reviews_reputation",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/podium",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "birdeye",
    name: "Birdeye Reviews Pack",
    description: "Monitor and manage reviews via Birdeye.",
    category: "reviews_reputation",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/birdeye",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  // AI, ML, Search, Knowledge
  {
    id: "openai",
    name: "OpenAI API Pack",
    description: "Access OpenAI GPT models. Text generation, embeddings, vision.",
    category: "ai_ml",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["api"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/openai",
    enabled: false,
    phase: "future",
  },
  {
    id: "anthropic",
    name: "Anthropic Claude Pack",
    description: "Access Anthropic Claude models. Advanced reasoning and analysis.",
    category: "ai_ml",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["api"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/anthropic",
    enabled: false,
    phase: "future",
  },
  {
    id: "google_gemini",
    name: "Google Gemini Pack",
    description: "Access Google Gemini models. Multimodal AI capabilities.",
    category: "ai_ml",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["api"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/google",
    enabled: false,
    phase: "future",
  },
  {
    id: "azure_openai",
    name: "Azure OpenAI Pack",
    description: "Access OpenAI models via Azure. Enterprise-grade AI.",
    category: "ai_ml",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["api"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/microsoftazure",
    enabled: false,
    phase: "future",
  },
  {
    id: "whisper",
    name: "Whisper Speech Pack",
    description: "Speech-to-text with OpenAI Whisper.",
    category: "ai_ml",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["api"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/openai",
    enabled: false,
    phase: "future",
  },
  {
    id: "google_speech",
    name: "Google Speech Pack",
    description: "Speech-to-text and text-to-speech with Google Cloud Speech.",
    category: "ai_ml",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["cloud-platform"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/google",
    enabled: false,
    phase: "future",
  },
  {
    id: "google_vision",
    name: "Google Vision Pack",
    description: "Image analysis with Google Cloud Vision. OCR, object detection.",
    category: "ai_ml",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["cloud-platform"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/google",
    enabled: false,
    phase: "future",
  },
  {
    id: "deepl",
    name: "DeepL Translation Pack",
    description: "High-quality translation with DeepL.",
    category: "ai_ml",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["api"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/deepl",
    enabled: false,
    phase: "future",
  },
  {
    id: "pinecone",
    name: "Pinecone Vector DB Pack",
    description: "Vector database with Pinecone. Store and search embeddings.",
    category: "ai_ml",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["api"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/pinecone",
    enabled: false,
    phase: "future",
  },
  {
    id: "weaviate",
    name: "Weaviate Vector DB Pack",
    description: "Vector database with Weaviate. Semantic search and RAG.",
    category: "ai_ml",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["api"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/weaviate",
    enabled: false,
    phase: "future",
  },
  {
    id: "serpapi",
    name: "SerpAPI Search Pack",
    description: "Google search results via SerpAPI. SEO and research.",
    category: "ai_ml",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["api"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/serpapi",
    enabled: false,
    phase: "future",
  },
  {
    id: "bing_search",
    name: "Bing Search Pack",
    description: "Web search with Bing Search API.",
    category: "ai_ml",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["api"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/bing",
    enabled: false,
    phase: "future",
  },
  {
    id: "brave_search",
    name: "Brave Search Pack",
    description: "Privacy-focused web search with Brave Search API.",
    category: "ai_ml",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["api"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/brave",
    enabled: false,
    phase: "future",
  },
  {
    id: "newsapi",
    name: "NewsAPI Pack",
    description: "Access news articles from NewsAPI.",
    category: "ai_ml",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["api"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/newsapi",
    enabled: false,
    phase: "future",
  },
  // Cloud, Infrastructure, DevOps
  {
    id: "aws_s3",
    name: "AWS S3 Storage Pack",
    description: "Store and retrieve files from AWS S3.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["s3"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/amazonaws",
    enabled: false,
    phase: "future",
  },
  {
    id: "aws_lambda",
    name: "AWS Lambda Pack",
    description: "Run serverless functions with AWS Lambda.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["lambda"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/amazonaws",
    enabled: false,
    phase: "future",
  },
  {
    id: "gcp_storage",
    name: "Google Cloud Storage Pack",
    description: "Store and retrieve files from Google Cloud Storage.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["storage"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/googlecloud",
    enabled: false,
    phase: "future",
  },
  {
    id: "gcp_bigquery",
    name: "Google BigQuery Pack",
    description: "Query data with Google BigQuery.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["bigquery"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/googlebigquery",
    enabled: false,
    phase: "future",
  },
  {
    id: "vercel",
    name: "Vercel Deployment Pack",
    description: "Deploy and manage applications on Vercel.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["deployments"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/vercel",
    enabled: false,
    phase: "future",
  },
  {
    id: "netlify",
    name: "Netlify Deployment Pack",
    description: "Deploy and manage applications on Netlify.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["deployments"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/netlify",
    enabled: false,
    phase: "future",
  },
  {
    id: "cloudflare_workers",
    name: "Cloudflare Workers Pack",
    description: "Run serverless functions with Cloudflare Workers.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["workers"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/cloudflare",
    enabled: false,
    phase: "future",
  },
  {
    id: "github_actions",
    name: "GitHub Actions Pack",
    description: "Automate workflows with GitHub Actions.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["actions"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/github",
    enabled: false,
    phase: "future",
  },
  {
    id: "datadog",
    name: "Datadog Monitoring Pack",
    description: "Monitor applications with Datadog.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/datadog",
    enabled: false,
    phase: "future",
  },
  {
    id: "sentry",
    name: "Sentry Error Tracking Pack",
    description: "Track errors and performance with Sentry.",
    category: "cloud_devops",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/sentry",
    enabled: false,
    phase: "future",
  },
  // Developer Platforms
  {
    id: "github",
    name: "GitHub Pack",
    description: "Manage GitHub repositories. Create issues, PRs, and manage code.",
    category: "developer",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["repo"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/github",
    enabled: false,
    phase: "future",
  },
  {
    id: "gitlab",
    name: "GitLab Pack",
    description: "Manage GitLab repositories. CI/CD and project management.",
    category: "developer",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["api"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/gitlab",
    enabled: false,
    phase: "future",
  },
  {
    id: "bitbucket",
    name: "Bitbucket Pack",
    description: "Manage Bitbucket repositories. Code and project management.",
    category: "developer",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["repository"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/bitbucket",
    enabled: false,
    phase: "future",
  },
  // Productivity - Additional
  {
    id: "notion",
    name: "Notion Workspace Pack",
    description: "Access and manage Notion workspaces. Read and write pages.",
    category: "productivity",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "update"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/notion",
    enabled: false,
    phase: "future",
  },
  {
    id: "airtable",
    name: "Airtable Base Pack",
    description: "Access and manage Airtable bases. Read and write records.",
    category: "productivity",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["data.records:read", "data.records:write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/airtable",
    enabled: false,
    phase: "future",
  },
  {
    id: "trello",
    name: "Trello Board Pack",
    description: "Manage Trello boards. Create cards and manage lists.",
    category: "productivity",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/trello",
    enabled: false,
    phase: "future",
  },
  {
    id: "asana",
    name: "Asana Workspace Pack",
    description: "Manage Asana projects and tasks.",
    category: "productivity",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/asana",
    enabled: false,
    phase: "future",
  },
  {
    id: "jira",
    name: "Jira Project Pack",
    description: "Manage Jira projects and issues.",
    category: "productivity",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/jira",
    enabled: false,
    phase: "future",
  },
  {
    id: "monday",
    name: "Monday.com Workspace Pack",
    description: "Manage Monday.com boards and items.",
    category: "productivity",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/monday",
    enabled: false,
    phase: "future",
  },
  {
    id: "clickup",
    name: "ClickUp Workspace Pack",
    description: "Manage ClickUp spaces and tasks.",
    category: "productivity",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/clickup",
    enabled: false,
    phase: "future",
  },
  // CRM, Sales, Support - Additional
  {
    id: "zoho",
    name: "Zoho CRM Pack",
    description: "Access Zoho CRM. View contacts, deals, and accounts.",
    category: "crm_sales",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/zoho",
    enabled: false,
    phase: "future",
  },
  {
    id: "pipedrive",
    name: "Pipedrive CRM Pack",
    description: "Access Pipedrive CRM. View deals and contacts.",
    category: "crm_sales",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/pipedrive",
    enabled: false,
    phase: "future",
  },
  {
    id: "zendesk",
    name: "Zendesk Support Pack",
    description: "Manage Zendesk tickets and support requests.",
    category: "crm_sales",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/zendesk",
    enabled: false,
    phase: "future",
  },
  {
    id: "intercom",
    name: "Intercom Support Pack",
    description: "Manage Intercom conversations and customers.",
    category: "crm_sales",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/intercom",
    enabled: false,
    phase: "future",
  },
  {
    id: "freshdesk",
    name: "Freshdesk Support Pack",
    description: "Manage Freshdesk tickets and support requests.",
    category: "crm_sales",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/freshdesk",
    enabled: false,
    phase: "future",
  },
  {
    id: "servicenow",
    name: "ServiceNow Pack",
    description: "Access ServiceNow. Enterprise service management.",
    category: "crm_sales",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/servicenow",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  // HR, Payroll
  {
    id: "workday",
    name: "Workday HR Pack",
    description: "Access Workday HR data. Enterprise HR management.",
    category: "hr_payroll",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/workday",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "bamboohr",
    name: "BambooHR Pack",
    description: "Access BambooHR. HR management and employee data.",
    category: "hr_payroll",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/bamboohr",
    enabled: false,
    phase: "future",
  },
  {
    id: "gusto",
    name: "Gusto Payroll Pack",
    description: "Access Gusto payroll data. Employee and payroll information.",
    category: "hr_payroll",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/gusto",
    enabled: false,
    phase: "future",
  },
  {
    id: "deputy",
    name: "Deputy Scheduling Pack",
    description: "Manage employee schedules with Deputy.",
    category: "hr_payroll",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/deputy",
    enabled: false,
    phase: "future",
  },
  {
    id: "adp",
    name: "ADP Payroll Pack",
    description: "Access ADP payroll data. Enterprise payroll management.",
    category: "hr_payroll",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/adp",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  // Education, Learning
  {
    id: "coursera",
    name: "Coursera Learning Pack",
    description: "Access Coursera course data. Limited API access.",
    category: "education",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/coursera",
    enabled: false,
    phase: "future",
  },
  {
    id: "udemy",
    name: "Udemy Business Pack",
    description: "Access Udemy Business course data. Enterprise learning.",
    category: "education",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/udemy",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "patreon",
    name: "Patreon Creator Pack",
    description: "Access Patreon creator data. Manage memberships and content.",
    category: "education",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/patreon",
    enabled: false,
    phase: "future",
  },
  // IoT, Smart Home
  {
    id: "home_assistant",
    name: "Home Assistant Pack",
    description: "Control Home Assistant devices. Smart home automation.",
    category: "iot",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/homeassistant",
    enabled: false,
    phase: "future",
  },
  {
    id: "philips_hue",
    name: "Philips Hue Pack",
    description: "Control Philips Hue lights. Smart lighting.",
    category: "iot",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/philips",
    enabled: false,
    phase: "future",
  },
  {
    id: "google_home",
    name: "Google Home Pack",
    description: "Control Google Home devices. Limited API access.",
    category: "iot",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/googlehome",
    enabled: false,
    phase: "future",
  },
  {
    id: "alexa_skills",
    name: "Alexa Skills Pack",
    description: "Create and manage Alexa skills. Voice automation.",
    category: "iot",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/amazonalexa",
    enabled: false,
    phase: "future",
  },
  {
    id: "smartthings",
    name: "SmartThings Pack",
    description: "Control SmartThings devices. Smart home automation.",
    category: "iot",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/smartthings",
    enabled: false,
    phase: "future",
  },
  // Vehicles, Telematics
  {
    id: "geotab",
    name: "Geotab Fleet Pack",
    description: "Access Geotab fleet telematics. Vehicle tracking and data.",
    category: "vehicles",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/geotab",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "tesla",
    name: "Tesla Vehicle Pack",
    description: "Access Tesla vehicle data. Unofficial API, subject to change.",
    category: "vehicles",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/tesla",
    enabled: false,
    phase: "future",
  },
  // Healthcare (Restricted)
  {
    id: "fhir",
    name: "FHIR Healthcare Pack",
    description: "Access FHIR healthcare data. Enterprise healthcare integration.",
    category: "healthcare",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/fhir",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "apple_healthkit",
    name: "Apple HealthKit Pack",
    description: "Access Apple HealthKit data. Health and fitness data.",
    category: "healthcare",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/apple",
    enabled: false,
    phase: "future",
  },
  {
    id: "google_fit",
    name: "Google Fit Pack",
    description: "Access Google Fit data. Health and fitness tracking.",
    category: "healthcare",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/googlefit",
    enabled: false,
    phase: "future",
  },
  {
    id: "garmin",
    name: "Garmin Health Pack",
    description: "Access Garmin health data. Fitness and activity tracking.",
    category: "healthcare",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/garmin",
    enabled: false,
    phase: "future",
  },
  {
    id: "oura",
    name: "Oura Ring Pack",
    description: "Access Oura Ring health data. Sleep and activity tracking.",
    category: "healthcare",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/oura",
    enabled: false,
    phase: "future",
  },
  {
    id: "whoop",
    name: "Whoop Pack",
    description: "Access Whoop health data. Fitness and recovery tracking.",
    category: "healthcare",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/whoop",
    enabled: false,
    phase: "future",
  },
  // Government / Public Data
  {
    id: "data_gov",
    name: "Data.gov Pack",
    description: "Access open government data from Data.gov.",
    category: "government",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/datagov",
    enabled: false,
    phase: "future",
  },
  {
    id: "business_registry",
    name: "Business Registry Pack",
    description: "Access business registry data. Company information.",
    category: "government",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/businessregistry",
    enabled: false,
    phase: "future",
  },
  // Automation
  {
    id: "zapier",
    name: "Zapier Automation Pack",
    description: "Connect to Zapier. Automate workflows across 5000+ apps.",
    category: "productivity",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/zapier",
    enabled: false,
    phase: "future",
  },
  {
    id: "make",
    name: "Make (Integromat) Pack",
    description: "Connect to Make. Visual workflow automation.",
    category: "productivity",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/integromat",
    enabled: false,
    phase: "future",
  },
  {
    id: "n8n",
    name: "n8n Automation Pack",
    description: "Connect to n8n. Self-hosted workflow automation.",
    category: "productivity",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/n8n",
    enabled: false,
    phase: "future",
  },
  // Additional Services
  {
    id: "magento",
    name: "Magento Store Pack",
    description: "Manage Magento stores. Enterprise e-commerce platform.",
    category: "ecommerce",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/magento",
    enabled: false,
    phase: "future",
  },
  {
    id: "prestashop",
    name: "PrestaShop Store Pack",
    description: "Manage PrestaShop stores. Open-source e-commerce.",
    category: "ecommerce",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/prestashop",
    enabled: false,
    phase: "future",
  },
  {
    id: "opencart",
    name: "OpenCart Store Pack",
    description: "Manage OpenCart stores. Open-source shopping cart.",
    category: "ecommerce",
    pricingBand: "power",
    unlockPriceUSD: 19.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/opencart",
    enabled: false,
    phase: "future",
  },
  {
    id: "vonage_sms",
    name: "Vonage SMS Pack",
    description: "Send and receive SMS with Vonage.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["sms"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/vonage",
    enabled: false,
    phase: "future",
  },
  {
    id: "messagebird",
    name: "MessageBird SMS Pack",
    description: "Send and receive SMS with MessageBird.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["sms"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/messagebird",
    enabled: false,
    phase: "future",
  },
  {
    id: "vonage_voice",
    name: "Vonage Voice Pack",
    description: "Make and receive voice calls with Vonage.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["voice"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/vonage",
    enabled: false,
    phase: "future",
  },
  {
    id: "telnyx",
    name: "Telnyx Communications Pack",
    description: "SMS, voice, and messaging with Telnyx.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["sms", "voice"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/telnyx",
    enabled: false,
    phase: "future",
  },
  {
    id: "ses",
    name: "AWS SES Email Pack",
    description: "Send transactional emails with AWS SES.",
    category: "messaging_voice",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["ses:SendEmail"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/amazonaws",
    enabled: false,
    phase: "future",
  },
  {
    id: "agora",
    name: "Agora Video Pack",
    description: "Real-time video and voice with Agora.",
    category: "messaging_voice",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/agora",
    enabled: false,
    phase: "future",
  },
  {
    id: "daily",
    name: "Daily Video Pack",
    description: "Real-time video with Daily.co.",
    category: "messaging_voice",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/daily",
    enabled: false,
    phase: "future",
  },
  {
    id: "twilio_lookup",
    name: "Twilio Lookup Pack",
    description: "Phone number intelligence with Twilio Lookup.",
    category: "messaging_voice",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["lookup"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/twilio",
    enabled: false,
    phase: "future",
  },
  {
    id: "meta_graph",
    name: "Meta Graph API Pack",
    description: "Access Facebook and Instagram via Meta Graph API.",
    category: "social_ads",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["pages_manage_posts", "instagram_basic"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/meta",
    enabled: false,
    phase: "future",
  },
  {
    id: "openstreetmap",
    name: "OpenStreetMap Pack",
    description: "Access OpenStreetMap data. Open-source mapping.",
    category: "maps_geo",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/openstreetmap",
    enabled: false,
    phase: "future",
  },
  {
    id: "nominatim",
    name: "Nominatim Geocoding Pack",
    description: "Geocoding with Nominatim. OpenStreetMap geocoding.",
    category: "maps_geo",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/nominatim",
    enabled: false,
    phase: "future",
  },
  {
    id: "meteostat",
    name: "Meteostat Weather Pack",
    description: "Access weather data from Meteostat.",
    category: "maps_geo",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/meteostat",
    enabled: false,
    phase: "future",
  },
  {
    id: "loqate",
    name: "Loqate Address Pack",
    description: "Address verification and geocoding with Loqate.",
    category: "maps_geo",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/loqate",
    enabled: false,
    phase: "future",
  },
  {
    id: "smarty",
    name: "Smarty Address Pack",
    description: "Address verification and geocoding with Smarty.",
    category: "maps_geo",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/smarty",
    enabled: false,
    phase: "future",
  },
  {
    id: "amadeus",
    name: "Amadeus Travel Pack",
    description: "Access Amadeus travel data. Flight and hotel booking.",
    category: "maps_geo",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/amadeus",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "sabre",
    name: "Sabre Travel Pack",
    description: "Access Sabre travel data. Enterprise travel booking.",
    category: "maps_geo",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/sabre",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "uber",
    name: "Uber Rideshare Pack",
    description: "Access Uber API. Rideshare integration (restricted).",
    category: "maps_geo",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/uber",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "yext",
    name: "Yext Local Pack",
    description: "Manage local business listings with Yext.",
    category: "reviews_reputation",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/yext",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "azure_speech",
    name: "Azure Speech Pack",
    description: "Speech-to-text and text-to-speech with Azure.",
    category: "ai_ml",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["api"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/microsoftazure",
    enabled: false,
    phase: "future",
  },
  {
    id: "azure_vision",
    name: "Azure Vision Pack",
    description: "Image analysis with Azure Computer Vision.",
    category: "ai_ml",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["api"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/microsoftazure",
    enabled: false,
    phase: "future",
  },
  {
    id: "aws_rekognition",
    name: "AWS Rekognition Pack",
    description: "Image and video analysis with AWS Rekognition.",
    category: "ai_ml",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["rekognition"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/amazonaws",
    enabled: false,
    phase: "future",
  },
  {
    id: "microsoft_translate",
    name: "Microsoft Translate Pack",
    description: "Translation with Microsoft Translator.",
    category: "ai_ml",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["api"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/microsoft",
    enabled: false,
    phase: "future",
  },
  {
    id: "milvus",
    name: "Milvus Vector DB Pack",
    description: "Vector database with Milvus. Semantic search.",
    category: "ai_ml",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["api"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/milvus",
    enabled: false,
    phase: "future",
  },
  {
    id: "qdrant",
    name: "Qdrant Vector DB Pack",
    description: "Vector database with Qdrant. Semantic search.",
    category: "ai_ml",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["api"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/qdrant",
    enabled: false,
    phase: "future",
  },
  {
    id: "crossref",
    name: "Crossref Academic Pack",
    description: "Access academic publications via Crossref.",
    category: "ai_ml",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/crossref",
    enabled: false,
    phase: "future",
  },
  {
    id: "semantic_scholar",
    name: "Semantic Scholar Pack",
    description: "Access academic papers via Semantic Scholar.",
    category: "ai_ml",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/semanticscholar",
    enabled: false,
    phase: "future",
  },
  {
    id: "gdelt",
    name: "GDELT News Pack",
    description: "Access global news data from GDELT.",
    category: "ai_ml",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/gdelt",
    enabled: false,
    phase: "future",
  },
  {
    id: "aws_ec2",
    name: "AWS EC2 Pack",
    description: "Manage AWS EC2 instances. Cloud computing.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["ec2"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/amazonaws",
    enabled: false,
    phase: "future",
  },
  {
    id: "aws_sqs",
    name: "AWS SQS Pack",
    description: "Message queuing with AWS SQS.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["sqs"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/amazonaws",
    enabled: false,
    phase: "future",
  },
  {
    id: "aws_sns",
    name: "AWS SNS Pack",
    description: "Notifications with AWS SNS.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["sns"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/amazonaws",
    enabled: false,
    phase: "future",
  },
  {
    id: "gcp_compute",
    name: "Google Cloud Compute Pack",
    description: "Manage GCP compute instances.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["compute"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/googlecloud",
    enabled: false,
    phase: "future",
  },
  {
    id: "azure_arm",
    name: "Azure ARM Pack",
    description: "Manage Azure resources with ARM.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["arm"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/microsoftazure",
    enabled: false,
    phase: "future",
  },
  {
    id: "azure_blob",
    name: "Azure Blob Storage Pack",
    description: "Store files in Azure Blob Storage.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["blob"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/microsoftazure",
    enabled: false,
    phase: "future",
  },
  {
    id: "azure_functions",
    name: "Azure Functions Pack",
    description: "Run serverless functions with Azure Functions.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["functions"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/microsoftazure",
    enabled: false,
    phase: "future",
  },
  {
    id: "cloudflare_dns",
    name: "Cloudflare DNS Pack",
    description: "Manage DNS with Cloudflare.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["dns"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/cloudflare",
    enabled: false,
    phase: "future",
  },
  {
    id: "cloudflare_waf",
    name: "Cloudflare WAF Pack",
    description: "Manage WAF rules with Cloudflare.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["waf"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/cloudflare",
    enabled: false,
    phase: "future",
  },
  {
    id: "route53",
    name: "Route53 DNS Pack",
    description: "Manage DNS with AWS Route53.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["route53"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/amazonaws",
    enabled: false,
    phase: "future",
  },
  {
    id: "docker_hub",
    name: "Docker Hub Pack",
    description: "Manage Docker images on Docker Hub.",
    category: "cloud_devops",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/docker",
    enabled: false,
    phase: "future",
  },
  {
    id: "kubernetes",
    name: "Kubernetes Pack",
    description: "Manage Kubernetes clusters.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["api"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/kubernetes",
    enabled: false,
    phase: "future",
  },
  {
    id: "gitlab_ci",
    name: "GitLab CI Pack",
    description: "Automate CI/CD with GitLab CI.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["api"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/gitlab",
    enabled: false,
    phase: "future",
  },
  {
    id: "circleci",
    name: "CircleCI Pack",
    description: "Automate CI/CD with CircleCI.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["api"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/circleci",
    enabled: false,
    phase: "future",
  },
  {
    id: "new_relic",
    name: "New Relic Monitoring Pack",
    description: "Monitor applications with New Relic.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/newrelic",
    enabled: false,
    phase: "future",
  },
  {
    id: "launchdarkly",
    name: "LaunchDarkly Feature Flags Pack",
    description: "Manage feature flags with LaunchDarkly.",
    category: "cloud_devops",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/launchdarkly",
    enabled: false,
    phase: "future",
  },
  {
    id: "npm",
    name: "npm Registry Pack",
    description: "Access npm package registry.",
    category: "developer",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/npm",
    enabled: false,
    phase: "future",
  },
  {
    id: "pypi",
    name: "PyPI Registry Pack",
    description: "Access PyPI package registry.",
    category: "developer",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/pypi",
    enabled: false,
    phase: "future",
  },
  {
    id: "maven",
    name: "Maven Repository Pack",
    description: "Access Maven repositories.",
    category: "developer",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/apache",
    enabled: false,
    phase: "future",
  },
  {
    id: "github_packages",
    name: "GitHub Packages Pack",
    description: "Access GitHub Packages registry.",
    category: "developer",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["packages"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/github",
    enabled: false,
    phase: "future",
  },
  {
    id: "logtail",
    name: "Logtail Logging Pack",
    description: "Centralized logging with Logtail.",
    category: "developer",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/logtail",
    enabled: false,
    phase: "future",
  },
  {
    id: "google_play",
    name: "Google Play Developer Pack",
    description: "Manage Google Play apps and listings.",
    category: "developer",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/googleplay",
    enabled: false,
    phase: "future",
  },
  {
    id: "app_store_connect",
    name: "App Store Connect Pack",
    description: "Manage App Store apps and listings.",
    category: "developer",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/appstore",
    enabled: false,
    phase: "future",
  },
  {
    id: "google_docs",
    name: "Google Docs Pack",
    description: "Access and manage Google Docs.",
    category: "productivity",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["docs"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/googledocs",
    enabled: false,
    phase: "future",
  },
  {
    id: "google_sheets",
    name: "Google Sheets Pack",
    description: "Access and manage Google Sheets.",
    category: "productivity",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["spreadsheets"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/googlesheets",
    enabled: false,
    phase: "future",
  },
  {
    id: "onedrive",
    name: "OneDrive Pack",
    description: "Access and manage OneDrive files.",
    category: "productivity",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["files.readwrite"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/microsoftonedrive",
    enabled: false,
    phase: "future",
  },
  {
    id: "sharepoint",
    name: "SharePoint Pack",
    description: "Access and manage SharePoint sites.",
    category: "productivity",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["sites.readwrite"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/microsoftsharepoint",
    enabled: false,
    phase: "future",
  },
  {
    id: "confluence",
    name: "Confluence Pack",
    description: "Access and manage Confluence pages.",
    category: "productivity",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/confluence",
    enabled: false,
    phase: "future",
  },
  {
    id: "tink",
    name: "Tink Banking Pack",
    description: "Connect to bank accounts via Tink. Open Banking.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["accounts", "transactions"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/tink",
    enabled: false,
    phase: "future",
  },
  {
    id: "mx",
    name: "MX Banking Pack",
    description: "Connect to bank accounts via MX. Financial data aggregation.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["accounts", "transactions"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/mx",
    enabled: false,
    phase: "future",
  },
  {
    id: "tradier",
    name: "Tradier Trading Pack",
    description: "Connect to Tradier trading platform.",
    category: "payments_finance",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/tradier",
    enabled: false,
    phase: "future",
  },
  {
    id: "twilio_live",
    name: "Twilio Live Pack",
    description: "Live video streaming with Twilio Live.",
    category: "messaging_voice",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/twilio",
    enabled: false,
    phase: "future",
  },
  {
    id: "numverify",
    name: "Numverify Phone Pack",
    description: "Phone number validation with Numverify.",
    category: "messaging_voice",
    pricingBand: "micro",
    unlockPriceUSD: 4.99,
    requiredSubscriptionTier: "personal",
    riskLevel: "low",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: false,
    icon: "https://cdn.simpleicons.org/numverifyphone",
    enabled: false,
    phase: "future",
  },
  {
    id: "youtube_upload",
    name: "YouTube Upload Pack",
    description: "Upload videos to YouTube. Manage channel.",
    category: "media_entertainment",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["youtube.upload"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/youtube",
    enabled: false,
    phase: "future",
  },
  {
    id: "rudderstack",
    name: "RudderStack CDP Pack",
    description: "Customer data platform with RudderStack.",
    category: "social_ads",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/rudderstack",
    enabled: false,
    phase: "future",
  },
  {
    id: "okta_scim",
    name: "Okta SCIM Pack",
    description: "User provisioning with Okta SCIM.",
    category: "hr_payroll",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["scim"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/okta",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "azure_ad_scim",
    name: "Azure AD SCIM Pack",
    description: "User provisioning with Azure AD SCIM.",
    category: "hr_payroll",
    pricingBand: "authority",
    unlockPriceUSD: 39.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read", "write"],
    permissionScopes: ["scim"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/microsoftazure",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "discord_community",
    name: "Discord Community Pack",
    description: "Manage Discord communities and servers.",
    category: "education",
    pricingBand: "power",
    unlockPriceUSD: 9.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "low",
    actionTypes: ["read", "write"],
    permissionScopes: ["bot", "guilds"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/discord",
    enabled: false,
    phase: "future",
  },
  {
    id: "memberstack",
    name: "Memberstack Pack",
    description: "Manage memberships with Memberstack.",
    category: "education",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/memberstack",
    enabled: false,
    phase: "future",
  },
  {
    id: "mqtt",
    name: "MQTT Broker Pack",
    description: "Connect to MQTT brokers. IoT messaging.",
    category: "iot",
    pricingBand: "power",
    unlockPriceUSD: 14.99,
    requiredSubscriptionTier: "pro",
    riskLevel: "medium",
    actionTypes: ["read", "write"],
    permissionScopes: ["read", "write"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/mqtt",
    enabled: false,
    phase: "future",
  },
  {
    id: "fleet_management",
    name: "Fleet Management Pack",
    description: "Access fleet management platforms. Vehicle tracking.",
    category: "vehicles",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/fleetmanagement",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "hl7",
    name: "HL7 Healthcare Pack",
    description: "Access HL7 healthcare data. Enterprise healthcare integration.",
    category: "healthcare",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/hl7",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "epic",
    name: "Epic Healthcare Pack",
    description: "Access Epic healthcare systems. Enterprise EHR.",
    category: "healthcare",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/epic",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "cerner",
    name: "Cerner Healthcare Pack",
    description: "Access Cerner healthcare systems. Enterprise EHR.",
    category: "healthcare",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/cerner",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
  {
    id: "court_database",
    name: "Court Database Pack",
    description: "Access court and legal databases. Restricted access.",
    category: "government",
    pricingBand: "authority",
    unlockPriceUSD: 49.99,
    requiredSubscriptionTier: "business",
    riskLevel: "high",
    actionTypes: ["read"],
    permissionScopes: ["read"],
    requiresApproval: true,
    icon: "https://cdn.simpleicons.org/courtdatabase",
    enabled: false,
    phase: "future",
    partnerOnly: true,
  },
];

/**
 * All capabilities combined - ALL 200+ APIs
 */
export const ALL_CAPABILITIES: Capability[] = [
  ...PHASE_1_CAPABILITIES,
  ...PHASE_2_CAPABILITIES,
  ...FUTURE_CAPABILITIES,
];

/**
 * Get capability by ID
 */
export function getCapability(capabilityId: string): Capability | null {
  return ALL_CAPABILITIES.find((c) => c.id === capabilityId) || null;
}

/**
 * Get capabilities by category
 */
export function getCapabilitiesByCategory(category: CapabilityCategory): Capability[] {
  return ALL_CAPABILITIES.filter((c) => c.category === category);
}

/**
 * Get capabilities by pricing band
 */
export function getCapabilitiesByPricingBand(band: "micro" | "power" | "authority"): Capability[] {
  return ALL_CAPABILITIES.filter((c) => c.pricingBand === band);
}

/**
 * Get capabilities by phase
 */
export function getCapabilitiesByPhase(phase: 1 | 2 | "future"): Capability[] {
  return ALL_CAPABILITIES.filter((c) => c.phase === phase);
}

/**
 * Get capabilities available for subscription tier
 */
export function getCapabilitiesForTier(tier: "free" | "personal" | "pro" | "business"): Capability[] {
  const tierOrder = { free: 0, personal: 1, pro: 2, business: 3 };
  const userTierLevel = tierOrder[tier];
  
  return ALL_CAPABILITIES.filter((c) => {
    if (!c.requiredSubscriptionTier) return true;
    const requiredTierLevel = tierOrder[c.requiredSubscriptionTier];
    return userTierLevel >= requiredTierLevel;
  });
}

